import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { DocumentState } from 'app/store/documents/document.state';
import { Activity } from 'ng2-app/modules/activity/models/activity/activity.model';
import { Observable, Subject } from 'rxjs';
import { StatusDisplayPipe } from 'ng2-app/pipes/status-display/status-display.pipe';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';
import {
  SetSelectedTabByName,
  ViewDocumentReviewTaskByDocId
} from 'ng2-app/modules/today/state/user-action-items.action';

@Component({
  selector: 'borrower-activity',
  templateUrl: 'borrower-activity.component.html',
  styleUrls: ['borrower-activity.component.scss'],
  standalone: false
})
export class BorrowerActivityComponent implements OnInit, OnDestroy {
  @Input() borrower;
  @Input() activity: Activity;
  @Input() simplified: boolean = false;
  @Select( DocumentState.documentCategories ) documentCategories$: Observable<DocumentCategory[]>;
  @Select(LenderUserState.hasPermission('canViewBorrowerPaneDeals')) canViewBorrowerPaneDeals$: Observable<boolean>;

  destroyed$ = new Subject<boolean>();
  showComments = false;
  statusDisplayPipe = new StatusDisplayPipe();

  icon: string;
  title: string;
  subTitle: string;
  valid: boolean;
  link: string;
  linkText: string;
  linkAction: any;

  get activityConfig() {
    return {
      sms: {
        icon: 'message',
        title: 'Text received',
        subTitle: () => `from ${this.activity.meta.fromNumber}`,
        linkText: 'View',
        link: () => `${this.borrowerUrl()}?pane=messages&subpane=sms`,
        linkAction: (permissions) => (permissions.canViewTodayMessagingTabs
          ? () => {
              try {
                this.store.dispatch( new SetSelectedTabByName('sms') );
              } catch (error) {
                console.error('Error dispatching SetSelectedTabByName:', error);
              }
            }
          : false)
      },
      call: {
        icon: () => this.activity.meta.recordingUrl
            ? 'voicemail'
            : 'phone',
        title: () => this.activity.meta.recordingUrl
            ? 'Voicemail received'
            : 'Call received',
        subTitle: () => `from ${this.activity.meta.fromNumber}`,
      },
      document: {
        icon: 'insert_drive_file',
        title: 'Document(s) uploaded',
        subTitle: () => {
          const by = (this.activity.meta.category === 'creditReport'
            ? 'System Account'
            : this.activity.meta.uploadedBy === 'borrower'
              ? this.activity.meta.primaryContactName || this.activity.borrowerName
              : this.activity.meta.lenderUserName);
          return `by ${by}`;
        },
        linkText: 'View',
        linkAction: (permissions) => ( permissions.canViewNewTodayPageTodayTab
            ? this.goToDocumentReview
            : false
        ),
        link: (permissions) => ( !permissions.canViewNewTodayPageTodayTab
            ? this.documentsUrl()
            : false
        ),
      },
      assignment: {
        icon: 'linear_scale',
        title: 'Assigned to',
        subTitle: () => this.activity.meta.newLender ? this.activity.meta.newLender : '[?]'
      },
      status: {
        icon: 'subdirectory_arrow_right',
        title: () => `${this.activity.dealId ? 'Deal ' : ''} Status Changed`,
        subTitle: () => {
          const oldStatus = this.statusDisplayPipe.transform( this.activity.oldStatus ),
            newStatus = this.statusDisplayPipe.transform( this.activity.status );
          return `from ${oldStatus} to ${newStatus}`;
        },
        link: () => this.activity.dealId ? this.dealsTabUrl() : false,
        linkText: 'View',
        linkAction: (permissions) => {
          const isDeal = !!this.activity.dealId;
          return isDeal && permissions.canViewBorrowerPaneDeals
            ? this.goToBorrowerPaneDealsTab
            : false;
        },
      },
      message: {
        icon: 'email',
        title: 'Email received',
        subTitle: () => `from ${this.activity.meta.fromEmail}`,
        linkText: 'View',
        link: () => `${this.borrowerUrl()}?pane=messages`,
        linkAction: (permissions) => (permissions.canViewTodayMessagingTabs
          ? () => this.store.dispatch( new SetSelectedTabByName('email') )
          : false),
      },
      offerCreated: {
        icon: 'insert_drive_file',
        title: 'Offer received',
        subTitle: () => {
          const lenderName = this.activity.meta.loanProductLenderName
            || (this.activity.meta.loanProductLender
              ? this.activity.meta.loanProductLender.name
              : null);
          return `for ${this.activity.meta.productName} from ${lenderName}`;
        },
        link: () => this.dealsTabUrl(),
        linkText: 'View',
        linkAction: (permissions) => (permissions.canViewBorrowerPaneDeals
          ? this.goToBorrowerPaneDealsTab
          : false),
      }
    };
  }

  constructor(
    private eventBusService: EventBusService,
    private store: Store,
  ) {}

  ngOnInit() {
    if ( this.simplified ) {
      this.updateConfig();
    }
  }

  updateConfig() {
    const permissions = this.store.selectSnapshot(LenderUserState.hasPermissions([
      'canSeeTodayCallHistoryTab',
      'canViewTodayMessagingTabs',
      'canViewTodayUserDropDown',
      'canViewNewTodayPageTodayTab',
      'canViewBorrowerPaneDeals',
    ]));
    const config = this.activityConfig[this.activity.type];
    if ( !config ) {
      this.valid = false;
      return;
    }
    [ 'icon', 'title', 'subTitle', 'link', 'linkText' ].forEach( key => {
      this[key] = typeof config[key] === 'function'
        ? config[key](permissions)
        : config[key];
    } );

    this.linkAction = typeof config.linkAction === 'function' ? config.linkAction(permissions) : false;

    this.valid = true;
  }

  goToBorrowerPaneDealsTab(): void {
    try {
      this.eventBusService.publish( new this.eventBusService.types.ChangeBorrowerPaneTabEvent('deals') );
    } catch (error) {
      console.error('Error in goToBorrowerPaneDealsTab:', error);
    }
  }

  goToDocumentReview(): void {
    try {
      if(this.activity.meta.documentId) {
        this.store.dispatch( new ViewDocumentReviewTaskByDocId(this.activity.meta.documentId) );
      }
    } catch (error) {
      console.error('Error in goToDocumentReview:', error);
    }
  }

  safeLinkAction(): void {
    try {
      if (this.linkAction && typeof this.linkAction === 'function') {
        this.linkAction();
      }
    } catch (error) {
      console.error('Error in linkAction:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private borrowerUrl() {
    return `/borrower/${this.activity.borrowerId}`;
  }

  private dealsTabUrl() {
    return `${this.borrowerUrl()}?pane=deals&subpane=active`;
  }

  private documentsUrl() {
    return `${this.borrowerUrl()}?pane=documents`;
  }
}
